import { useEffect, useState } from 'react';
import { FormHelperText, Grid, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import { FormDrawer, FormDrawerGroup, SwitchField } from '../../../../components/form-drawer';
import { RichTextEditor } from '../../components/rich-text-editor';
import { ImageUploadField } from '../../components/image-upload-field';
import { isRichTextEmpty } from '../../components/rich-text-utils';
import { resolveDateValue, toDateInputValue } from '../../../../utils/date';
import { INewsAddEditRequest } from '../../../../model/apm/news/request/news-add-edit-request';
import { INewsResponse } from '../../../../model/apm/news/response/news-response';
import { INewsCategoryResponse } from '../../../../model/apm/news/response/news-category-response';

interface NewsDrawerProps {
    open: boolean;
    initial?: INewsResponse;
    categories: INewsCategoryResponse[];
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: INewsAddEditRequest) => void;
}

const emptyValue: INewsAddEditRequest = { isHighlighted: false };

export function NewsDrawer({ open, initial, categories, isSaving, onClose, onSave }: NewsDrawerProps) {
    const [form, setForm] = useState<INewsAddEditRequest>(emptyValue);
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const isEdit = !!initial;
    const titleError = showValidationErrors && !form.title?.trim();
    const categoryError = showValidationErrors && !form.categoryId;
    const textError = showValidationErrors && isRichTextEmpty(form.text);

    useEffect(() => {
        if (!open) return;
        setForm(initial
            ? {
                title: initial.title,
                categoryId: initial.categoryId,
                date: toDateInputValue(initial.date),
                publishDate: toDateInputValue(initial.publishDate),
                source: initial.source,
                summary: initial.summary,
                text: initial.text,
                image: initial.image,
                isHighlighted: initial.isHighlighted,
            }
            : { ...emptyValue, date: toDateInputValue(new Date().toISOString()) });
        setShowValidationErrors(false);
    }, [open, initial]);

    function update(patch: Partial<INewsAddEditRequest>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        if (!form.title?.trim() || !form.categoryId || isRichTextEmpty(form.text)) {
            setShowValidationErrors(true);
            return;
        }
        onSave({
            ...form,
            title: form.title.trim(),
            source: form.source?.trim() || undefined,
            summary: form.summary?.trim() || undefined,
            date: resolveDateValue(form.date, initial?.date),
            publishDate: form.publishDate || undefined,
        });
    }

    // Notícias antigas podem estar em categoria que não é mais listada; mantém a opção para não perder o vínculo
    const categoryOptions = initial && !categories.some((c) => c.id === initial.categoryId)
        ? [...categories, { id: initial.categoryId, description: initial.category || `Categoria ${initial.categoryId}` }]
        : categories;

    return (
        <FormDrawer
            open={open}
            icon={<NewspaperIcon />}
            title={isEdit ? 'Editar notícia' : 'Nova notícia'}
            subtitle={isEdit ? initial?.title : 'A notícia é criada como rascunho; publique-a pela listagem'}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar notícia'}
            isSaving={isSaving}
            saveDisabled={isUploadingImage}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Identificação">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel required>Título</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoFocus
                            autoComplete="off"
                            inputProps={{ maxLength: 250 }}
                            value={form.title || ''}
                            onChange={(e) => update({ title: e.target.value })}
                            error={titleError}
                        />
                        {titleError && <FormHelperText error>Título é obrigatório</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Categoria</InputLabel>
                        <Select
                            fullWidth
                            value={form.categoryId ?? ''}
                            onChange={(e) => update({ categoryId: e.target.value === '' ? undefined : Number(e.target.value) })}
                            error={categoryError}
                        >
                            {categoryOptions.map((c) => <MenuItem key={c.id} value={c.id}>{c.description}</MenuItem>)}
                        </Select>
                        {categoryError
                            ? <FormHelperText error>Categoria é obrigatória</FormHelperText>
                            : categoryOptions.length === 0 && <FormHelperText>Nenhuma categoria de notícia cadastrada para a entidade</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel>Data da notícia</InputLabel>
                        <OutlinedInput fullWidth type="date" value={form.date || ''} onChange={(e) => update({ date: e.target.value || undefined })} />
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel>Fonte</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoComplete="off"
                            inputProps={{ maxLength: 100 }}
                            placeholder="Ex.: Assessoria de Comunicação"
                            value={form.source || ''}
                            onChange={(e) => update({ source: e.target.value })}
                        />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Conteúdo" description="Resumo exibido nas listagens do portal e texto completo da notícia.">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel>Resumo</InputLabel>
                        <OutlinedInput fullWidth multiline minRows={2} value={form.summary || ''} onChange={(e) => update({ summary: e.target.value })} />
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel required>Texto</InputLabel>
                        <RichTextEditor value={form.text} onChange={(text) => update({ text })} error={textError} minHeight={220} />
                        {textError && <FormHelperText error>Texto é obrigatório</FormHelperText>}
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel>Imagem de destaque</InputLabel>
                        <ImageUploadField
                            id="news-image"
                            value={form.image}
                            onChange={(image) => update({ image })}
                            onUploadingChange={setIsUploadingImage}
                        />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Exibição">
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                        <InputLabel>Publicar a partir de</InputLabel>
                        <OutlinedInput fullWidth type="date" value={form.publishDate || ''} onChange={(e) => update({ publishDate: e.target.value || undefined })} />
                        <FormHelperText>Opcional: sem data, a notícia aparece assim que for publicada</FormHelperText>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SwitchField
                            label="Notícia em destaque"
                            description="Notícias em destaque recebem posição de evidência no portal."
                            checked={form.isHighlighted}
                            onChange={(isHighlighted) => update({ isHighlighted })}
                        />
                    </Grid>
                </Grid>
            </FormDrawerGroup>
        </FormDrawer>
    );
}
